// ─────────────────────────────────────────────────────────────
// Fetches the platform service flags once on app load.
// Re-fetches every 5 minutes so toggles apply without reload.
//
// Usage:
//   const { isEnabled, services, loading } = useServices();
//   if (!isEnabled("trading")) return <ServiceGate serviceKey="trading" />;
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { getPublicServices } from "../api/apiServices";

export function useServices({ pollInterval = 5 * 60_000 } = {}) {
  const [services, setServices] = useState({});  // { trading: { enabled, name, disabledMessage }, ... }
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const initialised             = useRef(false);
  const timerRef                = useRef(null);

  const fetch_ = useCallback(async () => {
    if (!initialised.current) setLoading(true);
    setError(null);
    try {
      const res = await getPublicServices();
      if (res.status) setServices(res.data || {});
    } catch (err) {
      setError(err?.message || "Failed to load services");
    } finally {
      setLoading(false);
      initialised.current = true;
    }
  }, []);

  useEffect(() => {
    fetch_();
    timerRef.current = setInterval(fetch_, pollInterval);
    return () => clearInterval(timerRef.current);
  }, [fetch_, pollInterval]);

  /**
   * Check if a service is enabled.
   * Returns true by default if the service key is unknown
   * (fail-open so a missing key doesn't block the whole app).
   */
  const isEnabled = (key) => {
    if (loading) return true;              // show UI while loading
    if (!(key in services)) return true;  // unknown key → allow
    return services[key].enabled;
  };

  /**
   * Get the disabled message for a service.
   */
  const getMessage = (key) => {
    return services[key]?.disabledMessage || "This service is temporarily unavailable.";
  };

  return { services, loading, error, isEnabled, getMessage, refresh: fetch_ };
}
